import { Columns3, ChevronsUpDown } from 'lucide-react'
import { Table, Column } from '@tanstack/react-table'
import { useState } from 'react'

import { Button } from '../../primitives/Button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

interface ColumnSelectorFilterProps<TData> {
  /**
   * El objeto table de TanStack (opcional, usa esto O columns)
   */
  table?: Table<TData>
  /**
   * Array de columnas (opcional, usa esto O table)
   */
  columns?: Column<TData, unknown>[]
  /**
   * Función opcional para traducir nombres de columnas
   */
  getColumnDisplayName?: (_columnId: string) => string
  /**
   * Título del popover
   */
  title?: string
}

/**
 * ColumnSelectorFilter - Selector de columnas visibles de una tabla
 * Componente reutilizable para mostrar/ocultar columnas de tablas TanStack
 */
export function ColumnSelectorFilter<TData>({
  table,
  columns,
  getColumnDisplayName,
  title = 'Mostrar columnas',
}: ColumnSelectorFilterProps<TData>) {
  const [open, setOpen] = useState(false)

  const defaultGetColumnDisplayName = (columnId: string): string => {
    // Capitalizar primera letra por defecto
    return columnId.charAt(0).toUpperCase() + columnId.slice(1)
  }

  const displayNameFn = getColumnDisplayName || defaultGetColumnDisplayName

  // Obtener columnas del table o del array columns
  const columnsToShow = columns || (table ? table.getAllColumns() : [])
  const hidableColumns = columnsToShow.filter((column) => column.getCanHide())

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'h-9 min-w-fit shrink-0 justify-start px-3 font-normal text-[#262626]',
            'border-[#D4D4D4] bg-[#FAFAFA] text-sm shadow-[0_1px_2px_rgba(0,0,0,0.05)]',
            'hover:bg-[#F5F5F5] active:bg-[#E5E5E5]'
          )}
          icon={<Columns3 className="h-4 w-4" />}
          iconPosition="left"
          size="sm"
          variant="outline"
        >
          <span className="flex items-center gap-2">
            Columnas
            <ChevronsUpDown className="h-4 w-4" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[192px] min-w-[147px] p-0"
        style={{
          background: '#FAFAFA',
          border: '1px solid #D4D4D4',
          boxShadow:
            '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1)',
          borderRadius: '6px',
        }}
      >
        <div
          className="flex flex-col items-start"
          style={{ padding: 0, gap: 0 }}
        >
          {/* Header - Frame 3 */}
          <div
            className="flex w-full flex-col items-start"
            style={{ padding: '0px 8px', gap: '4px', height: '36px' }}
          >
            {/* Label */}
            <div
              className="flex h-[36px] w-full flex-row items-center"
              style={{ padding: '0px', gap: '4px' }}
            >
              <span
                className="flex-grow text-[14px] font-medium leading-5 tracking-[0.005em]"
                style={{ color: '#262626' }}
              >
                {title}
              </span>
            </div>
          </div>
          {/* Separator */}
          <div
            className="flex w-full items-center"
            style={{
              padding: '0px',
              height: '1px',
              background: '#FAFAFA',
              borderRadius: '2px',
            }}
          >
            <div
              style={{
                width: '100%',
                height: '1px',
                background: '#D4D4D4',
              }}
            />
          </div>
          {/* Items container */}
          <div
            className="flex w-full touch-pan-y flex-col items-start overflow-y-auto"
            onTouchMove={(event) => event.stopPropagation()}
            onTouchStart={(event) => event.stopPropagation()}
            style={{
              padding: '8px',
              gap: '0px',
              maxHeight: '272px',
              WebkitOverflowScrolling: 'touch',
              overscrollBehavior: 'contain',
            }}
          >
            {hidableColumns.map((column) => {
              const isVisible = column.getIsVisible()
              return (
                <div
                  className="flex w-full cursor-pointer items-center hover:bg-white/50"
                  key={column.id}
                  onClick={() => {
                    column.toggleVisibility(!isVisible)
                    // Cerrar el popover después de cambiar la visibilidad
                    setOpen(false)
                  }}
                  style={{
                    padding: '4px 8px',
                    gap: '8px',
                    height: '32px',
                    minHeight: '32px',
                    background: '#FAFAFA',
                    borderRadius: '6px',
                  }}
                >
                  {/* AL - Content container */}
                  <div
                    className="flex items-center"
                    style={{ gap: '8px', height: '20px' }}
                  >
                    {/* Decoration container 20x20 */}
                    <div
                      className="flex items-center justify-center"
                      style={{ width: '20px', height: '20px' }}
                    >
                      <Checkbox
                        checked={isVisible}
                        className={cn(
                          'h-4 w-4',
                          'data-[state=checked]:border-[#1E293B]',
                          'data-[state=checked]:bg-[#1E293B]',
                          'data-[state=checked]:text-[#FAFAFA]'
                        )}
                        id={`${column.id}-checkbox`}
                        onCheckedChange={(value) => {
                          column.toggleVisibility(!!value)
                          // Cerrar el popover después de cambiar la visibilidad
                          setOpen(false)
                        }}
                      />
                    </div>
                    {/* Label container */}
                    <div
                      className="flex flex-col items-start justify-center"
                      style={{ padding: '0px', height: '20px', flexGrow: 1 }}
                    >
                      <label
                        className="cursor-pointer text-[14px] font-normal leading-5 tracking-[0.005em]"
                        htmlFor={`${column.id}-checkbox`}
                        style={{ color: '#262626', height: '20px' }}
                      >
                        {typeof column.columnDef.header === 'string'
                          ? column.columnDef.header
                          : displayNameFn(column.id)}
                      </label>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
